import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import i18next from "i18next";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type ObjPathProxy, createProxy, getPath } from "ts-object-path";

export type ResourcePathType = ObjPathProxy<ResourceDictionary, string>;

export const Resources = createProxy<ResourceDictionary>();

function resolvePath(obj: any, path: string): string | null {
  const keys = path.split(".");
  const value = keys.reduce((current, key) => current?.[key], obj);

  return value ?? null;
}

export function getResourcePath(proxy: ResourcePathType): string {
  const path = getPath(proxy);

  if (!path || path.length === 0) return "";
  if (path.length === 1) return path[0].toString();
  return `${path[0].toString()}:${path.slice(1).join(".")}`;
}

export function useResource() {
  const { t: i18Translation } = useTranslation();

  // resolve string to Path
  const resolveResourcePath = useCallback((value: string): string => {
    const resourcesProperty = resolvePath({ Resources }, value);
    if (resourcesProperty) {
      return resourcesProperty;
    }

    return `|${value}|`;
  }, []);

  const t = useCallback(
    (resourcePath: ResourcePathType, options?: any) => i18Translation(getResourcePath(resourcePath), options) as unknown as string,
    [i18Translation],
  );

  return useMemo(() => ({ t, resolveResourcePath }), [t, resolveResourcePath]);
}

export function useServerError(parentObject: ObjPathProxy<ResourceDictionary, object>, fallbackProperty: ResourcePathType) {
  const { t } = useResource();
  return {
    translateError: (code: string | null | undefined, args?: Record<string, unknown> | null) => {
      if (!code) {
        return null;
      }

      const parentPath = getResourcePath(parentObject as any);
      // Use "." for nested keys (parent already has ":"), otherwise use ":" for first-level separation
      const separator = parentPath.includes(":") ? "." : ":";
      const newCode = `${parentPath}${separator}${code}`;

      const isCodeTranslated = i18next.exists(newCode);

      if (!isCodeTranslated) {
        console.log(`Not found serverError translation for ${newCode}`);
        logError(new Error(`Not found serverError translation for ${newCode}`), null, false);
      }

      const resource = isCodeTranslated ? i18next.t(newCode, args ?? undefined) : t(fallbackProperty);

      return resource as string;
    },
  };
}

export function useNsureServerError(fallbackProperty: ResourcePathType) {
  return useServerError(Resources.NsureErrors as unknown as ObjPathProxy<ResourceDictionary, object>, fallbackProperty);
}

export function getTranslation(resourcePath: ResourcePathType) {
  return i18next.t(getResourcePath(resourcePath));
}
